import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  TextField,
  Autocomplete,
  CircularProgress,
  Chip,
  InputAdornment
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { getManagers } from '../services/api';

const ManagerSelector = ({ 
  open, 
  onClose, 
  onConfirm, 
  isExporting = false 
}) => {
  const [selectedManager, setSelectedManager] = useState(null);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Загрузка менеджеров при открытии диалога
  useEffect(() => {
    if (open) {
      loadManagers();
    }
  }, [open]);

  const loadManagers = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await getManagers();
      // Фильтруем только активных менеджеров
      const activeManagers = (response.items || []).filter(m => m.is_active);
      setManagers(activeManagers);
    } catch (err) {
      console.error('Error loading managers:', err);
      setError('Ошибка загрузки списка менеджеров');
    } finally {
      setLoading(false);
    }
  };

  // Группировка менеджеров по филиалам
  const groupedOptions = useMemo(() => {
    return managers.sort((a, b) => {
      // Сначала сортируем по филиалу, потом по имени
      const baseCompare = (a.base_name || '').localeCompare(b.base_name || '', 'ru');
      if (baseCompare !== 0) return baseCompare;
      return (a.name || '').localeCompare(b.name || '', 'ru');
    });
  }, [managers]);

  // Фильтрация по поисковому запросу (локальная)
  const filterOptions = (options, { inputValue }) => {
    const searchLower = inputValue.toLowerCase().trim();
    if (!searchLower) return options;
    
    return options.filter(option => {
      const name = (option.name || '').toLowerCase();
      const baseName = (option.base_name || '').toLowerCase();
      const code = (option.code || '').toLowerCase();
      
      return name.includes(searchLower) || 
             baseName.includes(searchLower) || 
             code.includes(searchLower);
    });
  };

  const handleConfirm = () => {
    if (!selectedManager) {
      setError('Необходимо выбрать менеджера для экспорта в 1С');
      return;
    }

    // Передаём менеджера с маппингом на ожидаемую структуру
    onConfirm({
      id: selectedManager.code,
      name: selectedManager.name,
      base_name: selectedManager.base_name,
      base_code: selectedManager.base_code
    });
    handleClose();
  };

  const handleClose = () => {
    setSelectedManager(null);
    setSearchInput('');
    setError('');
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Typography variant="h6" component="div">
          Выбор менеджера для экспорта в 1С
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Для экспорта данных в 1С необходимо указать ответственного менеджера.
            Начните вводить имя или филиал для поиска.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Autocomplete
            value={selectedManager}
            onChange={(event, newValue) => {
              setSelectedManager(newValue);
              setError('');
            }}
            inputValue={searchInput}
            onInputChange={(event, newInputValue) => {
              setSearchInput(newInputValue);
            }}
            options={groupedOptions}
            groupBy={(option) => option.base_name || 'Без филиала'}
            getOptionLabel={(option) => option.name || ''}
            filterOptions={filterOptions}
            loading={loading}
            disabled={isExporting}
            isOptionEqualToValue={(option, value) => option.code === value.code}
            noOptionsText="Менеджеры не найдены"
            loadingText="Загрузка..."
            renderInput={(params) => (
              <TextField
                {...params}
                label="Поиск менеджера"
                placeholder="Введите имя, филиал или код..."
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => {
              const { key, ...restProps } = props;
              return (
                <Box
                  component="li"
                  key={option.code}
                  {...restProps}
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    py: 1.5
                  }}
                >
                  <PersonIcon color="action" fontSize="small" />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body1">
                      {option.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Код: {option.code}
                    </Typography>
                  </Box>
                </Box>
              );
            }}
            renderGroup={(params) => (
              <Box key={params.key}>
                <Box
                  sx={{
                    position: 'sticky',
                    top: -8,
                    px: 2,
                    py: 1,
                    backgroundColor: 'grey.100',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <BusinessIcon fontSize="small" color="primary" />
                  <Typography variant="subtitle2" color="primary" fontWeight="bold">
                    {params.group}
                  </Typography>
                  <Chip 
                    label={params.children.length} 
                    size="small" 
                    sx={{ ml: 'auto', height: 20 }}
                  />
                </Box>
                {params.children}
              </Box>
            )}
            sx={{ mb: 2 }}
          />

          {selectedManager && (
            <Alert severity="info" icon={<PersonIcon />}>
              <Typography variant="body2">
                <strong>Выбран:</strong> {selectedManager.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Филиал: {selectedManager.base_name} | Код: {selectedManager.code}
              </Typography>
            </Alert>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 3 }}>
        <Button 
          onClick={handleClose} 
          disabled={isExporting}
          color="inherit"
        >
          Отмена
        </Button>
        <Button 
          onClick={handleConfirm} 
          variant="contained" 
          color="success"
          disabled={isExporting || !selectedManager}
          sx={{ 
            minWidth: 150,
            bgcolor: '#27ae60',
            '&:hover': {
              bgcolor: '#229954'
            }
          }}
        >
          {isExporting ? 'Экспортируем...' : 'Экспортировать в 1С'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManagerSelector;
